import { chartsSettings } from "@/settings/charts";

type Transform = (input: string) => string;

interface Transforms {
  text: Transform;
  filter: Transform;
}

const collectFreeTextKeys = (node: unknown, acc: Set<string>): Set<string> => {
  if (Array.isArray(node)) {
    node.forEach((item) => collectFreeTextKeys(item, acc));
  } else if (node && typeof node === "object") {
    Object.entries(node).forEach(([key, value]) => {
      if (value && typeof value === "object" && (value as { type?: string }).type === "text") {
        acc.add(key);
      }
      collectFreeTextKeys(value, acc);
    });
  }

  return acc;
};

const freeTextKeys = collectFreeTextKeys(chartsSettings, new Set(["title"]));

const escapeApostrophes = (value: string) => value.replace(/'/g, "''");

const unescapeApostrophes = (value: string) => value.replace(/'{2,}/g, "'");

const escapeText = (value: string) => escapeApostrophes(value.replace(/\\/g, "\\\\").replace(/"/g, '\\"'));

const unescapeText = (value: string) => unescapeApostrophes(value).replace(/\\([\\"])/g, "$1");

const transformStrings = (value: unknown, transform: Transform): unknown => {
  if (typeof value === "string") {
    return transform(value);
  }

  if (Array.isArray(value)) {
    return value.map((item) => transformStrings(item, transform));
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, transformStrings(item, transform)]));
  }

  return value;
};

const transformUserInput = (value: unknown, transforms: Transforms): unknown => {
  if (Array.isArray(value)) {
    return value.map((item) => transformUserInput(item, transforms));
  }

  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value).map(([key, item]) => {
        if (key === "filterParams") {
          return [key, transformStrings(item, transforms.filter)];
        }

        if (typeof item === "string" && freeTextKeys.has(key)) {
          return [key, transforms.text(item)];
        }

        return [key, transformUserInput(item, transforms)];
      })
    );
  }

  return value;
};

export const escapeUserInput = <T>(settings: T): T =>
  transformUserInput(settings, { text: escapeText, filter: escapeApostrophes }) as T;

export const unescapeUserInput = <T>(settings: T): T =>
  transformUserInput(settings, { text: unescapeText, filter: unescapeApostrophes }) as T;
